import classNames from 'classnames';
import Image from 'next/image';
import { useLocale } from 'next-intl';
import React, { useMemo } from 'react';
import type { HitsProps } from 'react-instantsearch';
import { useHits } from 'react-instantsearch';

import { computeRelieurPhoto } from '@/components/ui/relieurs/utils/computeRelieurPhoto';
import { IMAGES_ASSETS_PREFIX } from '@/constants';
import { IMAGE_QUALITY_ENTITY_CARD } from '@/constants/image-quality';
import { Link } from '@/i18n/navigation';
import type { Meilisearch_Relieur } from '@/lib/api/meilisearch-types';
import type { PossibleLocales } from '@/utils/AppConfig';

interface RelieurCardProps {
  relieur: Meilisearch_Relieur;
}
export const RelieurCard: React.FC<RelieurCardProps> = ({ relieur }) => {
  const locale = useLocale() as PossibleLocales;
  const photo = useMemo(() => computeRelieurPhoto(relieur), [relieur]);
  const name =
    relieur.appellation_complete ??
    [relieur.prenom, relieur.nom].filter(Boolean).join(' ') ??
    '';
  return (
    <Link
      href={{
        pathname: `/relieurs/[slug]`,
        params: { slug: relieur.slug },
      }}
      key={`relieur-card-${relieur.slug}`}
      className="group card card-compact relative size-full overflow-hidden rounded-md bg-base-100 shadow-sm outline-none transition-all hover:bg-secondary-50 focus:outline-none focus-visible:outline-none active:scale-[.97]"
    >
      <figure className="relative aspect-[4/5] transition-all sm:group-hover:scale-105">
        {photo ? (
          <Image
            // src={`${IMAGES_ASSETS_PREFIX}/${selectionData.miniature_carree}`}
            src={`${IMAGES_ASSETS_PREFIX}/${photo}?fit=cover&width=400&height=500&quality=${IMAGE_QUALITY_ENTITY_CARD}`}
            alt={relieur.nom}
            fill
            quality={IMAGE_QUALITY_ENTITY_CARD}
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="absolute inset-0 bg-gray-200" />
        )}
      </figure>
      <div className="inset-x-0 bottom-0 z-[1] flex h-16 place-content-center items-center bg-white px-3 text-center font-gothic text-gray-700">
        <h5 className="text-center font-gothicb text-sm leading-tight">
          {name}
        </h5>
      </div>
    </Link>
  );
};

interface RelieursHitsProps extends HitsProps<Meilisearch_Relieur> {}
export const RelieursHits: React.FC<RelieursHitsProps> = (props) => {
  const { items } = useHits<Meilisearch_Relieur>(props);

  return (
    <>
      {items.map((hit, idx) => (
        <React.Fragment key={hit.slug}>
          <div
            style={{ '--eo-result-order': `${idx}` } as React.CSSProperties}
            className={classNames(
              'relative order-[var(--eo-result-order)]',
              'col-span-1 border-0',
            )}
          >
            <div
              style={{ display: 'var(--eo-search-scores-visibility, none)' }}
              className="absolute right-0 top-0 z-10 bg-black p-1 text-sm text-white"
            >
              {/* @ts-expect-error */}
              Score : {Number(hit._rankingScore * 100)?.toFixed(2)}
            </div>
            <div>
              <RelieurCard relieur={hit} />
            </div>
          </div>
        </React.Fragment>
      ))}
    </>
  );
};
